import React, { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import {
  subWeeks,
  startOfWeek,
  addWeeks,
  endOfWeek,
  format,
  parseISO,
  addDays,
  isBefore,
} from "date-fns";
import WeekSelector from "./WeekSelector.jsx";
import ConditionalRender from "./ConditionalRender";

import { useTranslation } from "react-i18next";
import ExportButton from "../components/ExportButton.tsx";
import ClaimGuard from "../components/Auth/ClaimGuard";
import {
  faChartLine,
  faTable,
  faDownload,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { stringToHslColor } from "../utils/msisdnUtils.js";
import {
  exportWeeklyConsumptionToExcel,
  exportConsumptionToExcelNew,
  ConsumptionPerMsisdn,
  exportRangeConsumptionToExcel,
} from "../utils/creditsUtils.tsx";

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  Legend,
} from "recharts";
import {
  Col,
  Container,
  FormControl,
  FormLabel,
  Row,
  FormCheck,
  FormSelect,
  Button,
  Table,
  ButtonGroup,
  ToggleButton,
} from "react-bootstrap";
import {
  getBundlePurchases,
  getCompanyConsumption,
  getMSISDNConsumption,
  getBalanceHistory,
  getConsumptionForMsisdn,
  fetchDataFromAllPages,
} from "../API/companyApi";
import { getMsisdnsForCompanyId } from "../API/msisdnApi";
import "react-datepicker/dist/react-datepicker.css";
import emitter from "eventBus";

import { GET_BILLING_REPORT, GET_BILLING_REPORT_ALL } from "../Claims";
// import './PurchaseHistoryChart.css'; // For optional custom styling
import css from "./css/ManagementCharts.module.css";
import Select from "react-select";

const ManagementCharts = ({ companyId, graphName, type, balance = null }) => {
  const { t } = useTranslation();

  const [startDate, setStartDate] = useState(new Date("2025-05-01"));
  const [endDate, setEndDate] = useState(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow;
  });

  const [selectedWeekId, setSelectedWeekId] = useState(null);
  const [weekRanges, setWeekRanges] = useState([]);
  const [isCustomRange, setIsCustomRange] = useState(false);

  const [data, setData] = useState([]);
  const [page, setPage] = useState(0);
  const [msisdns, setMsisdns] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [msisdnOptions, setMsisdnOptions] = useState([]);
  const [selectedMsisdns, setSelectedMsisdns] = useState([]);
  const [selectedMsisdnIds, setSelectedMsisdnIds] = useState([]);
  const [exportData, setExportData] = useState([]);
  const [viewMode, setViewMode] = useState("chart"); // "chart" or "table"
  const [balances, setBalances] = useState([]);
  const selectAllOption = {
    value: "all",
    label: t("creditManagement.selectAll"),
  };

  const [selectedWeek, setSelectedWeek] = useState(null);

  useEffect(() => {
    if (!startDate || !endDate) return;

    async function fetchData() {
      try {
        const range = `${format(startDate, "yyyy-MM-dd")}|${format(
          endDate,
          "yyyy-MM-dd"
        )}`;
        if (type == "purchase") {
          const response = await getBundlePurchases(companyId, range);
          const purchases = response.data;

          const formatted = purchases.map((item) => ({
            date: format(parseISO(item.purchaseDate), "dd MMM, HH:mm"),
            amount: item.amount / 100,
          }));
          setData(formatted);
        } else if (type == "consumption") {
          const response = await getCompanyConsumption(companyId, range);
          const records = response.data?.consumption || [];

          // Flatten and count per date
          const countsByDate = {};

          records.forEach((entry) => {
            entry.consumption.forEach((record) => {
              const date = format(parseISO(record.date), "dd MMM");
              countsByDate[date] = (countsByDate[date] || 0) + 1;
            });
          });

          const formatted = Object.entries(countsByDate).map(
            ([date, count]) => ({
              date,
              amount: count, // amount = count of events for bar height
            })
          );

          setData(formatted);
        }
        if (type === "msisdnConsumption") {
          for await (const msisdns of fetchDataFromAllPages(
            `v1/msisdn/company/${companyId}`,
            0,
            {}
          )) {
            var allOptions = [];
            msisdns.forEach((msisdn) => {
              const options = {
                value: msisdn.id,
                label: msisdn.id,
              };
              allOptions = allOptions.concat(options);
            });
            setMsisdnOptions([selectAllOption, ...allOptions]);
          }
        }
        if (type == "balance") {
          try {
            const response = await getBalanceHistory(companyId, range);
            const balances = response.data; // This is already an array
            setBalances(balances);
            const chartData = balances.map((entry) => ({
              date: entry.timestamp,
              amount: entry.balanceAfterModification,
            }));

            // Sort by timestamp
            chartData.sort((a, b) => new Date(a.date) - new Date(b.date));

            setData(chartData); // Array of { date, amount }
          } catch (err) {
            console.error("Error fetching balance history:", err);
          }
        }
      } catch (err) {
        console.error("Error fetching purchase history:", err);
      }
    }
    // fetchMsisdnS();
    fetchData();

    const refreshHandler = () => {
      fetchData();
    };

    emitter.on("refresh-balance", refreshHandler);

    return () => {
      emitter.off("refresh-balance", refreshHandler);
    };
  }, [companyId, startDate, endDate, selectedMsisdns, selectedWeek]);

  useEffect(() => {
    const today = new Date();
    const start = subWeeks(startOfWeek(today, { weekStartsOn: 1 }), 20);

    const ranges = [];
    for (let i = 0; i <= 20; i++) {
      const weekStart = addWeeks(start, i);
      const weekEnd = endOfWeek(weekStart, { weekStartsOn: 1 });

      ranges.push({
        id: i,
        startDate: weekStart,
        endDate: weekEnd,
        label: `${t("export.week")} ${format(weekStart, "w")} (${format(
          weekStart,
          "MMM d"
        )} - ${format(weekEnd, "MMM d")})`,
      });
    }
    ranges.push({ id: -1, label: t("creditManagement.custom") });

    setWeekRanges(ranges);

    // Default to current week (second last item)
    const defaultSelectedWeek = ranges.length - 2;
    setSelectedWeekId(defaultSelectedWeek);

    // Set start and end dates only if type is msisdnConsumption
    if (type === "msisdnConsumption") {
      const selectedRange = ranges[defaultSelectedWeek];
      if (selectedRange) {
        setStartDate(selectedRange.startDate);
        setEndDate(selectedRange.endDate);
      }
    }
  }, [t, type]);

  useEffect(() => {
    setSelectedWeek({ startDate, endDate });
  }, [startDate, endDate]);

  useEffect(() => {
    // onWeekSelected && onWeekSelected(selectedWeek)
    const startDatee = selectedWeek?.startDate;
    const endDatee = selectedWeek?.endDate;
  }, [selectedWeek]);

  useEffect(() => {
    fetchConsumptionReport(selectedMsisdns);
  }, [startDate, endDate, selectedMsisdns, selectedWeek]);

  async function fetchAllMsisdnsOptions(companyId) {
    // Fetch first page to get total pages
    const firstPageResponse = await getMsisdnsForCompanyId(companyId, 0);
    const firstPageData = firstPageResponse.data?.data;
    const totalPages = firstPageData?.totalPages || 1;

    // Map first page data to options
    let allOptions =
      firstPageData?.map((item) => ({
        value: item.id,
        label: item.id, // or use item.fullName if you want labels to be names
      })) || [];

    // Fetch other pages
    const pagePromises = [];
    for (let page = 2; page <= totalPages; page++) {
      pagePromises.push(getMsisdnsForCompanyId(companyId, page));
    }

    const otherPagesResponses = await Promise.all(pagePromises);

    for (const response of otherPagesResponses) {
      const pageData = response.data?.data || [];
      const options = pageData.map((item) => ({
        value: item.id,
        label: item.id,
      }));
      allOptions = allOptions.concat(options);
    }

    return allOptions;
  }

  async function fetchConsumptionReport(selectedMsisdnIds) {
    if (!selectedMsisdnIds || selectedMsisdnIds.length === 0) {
      setMsisdns([]);
      setData([]);
      setExportData({ consumption: [] });
      return 0;
    }
    if (!selectedWeek || !selectedWeek.startDate || !selectedWeek.endDate) {
      // handle missing date case, e.g., return or set a default range
      console.warn("Invalid selectedWeek or missing dates");
      return; // or handle gracefully
    }

    var msisdnsQuery = selectedMsisdnIds
      .map((entry) => `msisdns=${entry.value}`)
      .join("&");

    var range = `${format(selectedWeek?.startDate, "yyyy-MM-dd")}|${format(
      selectedWeek?.endDate,
      "yyyy-MM-dd"
    )}`;

    try {
      var msisdnsConsumption = await getConsumptionForMsisdn(
        companyId,
        range,
        msisdnsQuery
      );
      var result = [];
      msisdnsConsumption.data.consumption.forEach((element) => {
        element.consumption.forEach((item) => {
          result.push({
            msisdn: item.msisdn,
            date: item.date,
            amount: 1,
          });
        });
      });

      var selectedMsisdnStrings = selectedMsisdnIds.map((e) => e.value);
      // Pass startDate and endDate to transform data for full range
      var chartData = transformConsumptionData(
        result,
        selectedMsisdnStrings,
        selectedWeek?.startDate,
        selectedWeek?.endDate
      );
      if (msisdnsConsumption?.data?.consumption?.length > 0) {
        setExportData(msisdnsConsumption.data);
      } else {
        setExportData({ consumption: [] });
      }

      setData(chartData);

      var chartMsisdns = selectedMsisdnStrings;
      setMsisdns(chartMsisdns);
    } catch (error) {
      console.error("Failed to fetch consumption report:", error);
    }
  }

  async function fetchAllMsisdnOptions(companyId) {
    const firstPageResponse = await getMsisdnsForCompanyId(companyId, 0);
    const firstPageData = firstPageResponse.data?.data;
    const totalPages = firstPageData?.totalPages || 1;

    let allOptions =
      firstPageData?.map((item) => ({
        value: item.id,
        label: item.id,
      })) || [];

    const pagePromises = [];
    for (let page = 2; page <= totalPages; page++) {
      pagePromises.push(getMsisdnsForCompanyId(companyId, page));
    }

    const otherPagesResponses = await Promise.all(pagePromises);

    for (const response of otherPagesResponses) {
      const pageData = response.data?.data || [];
      const options = pageData.map((item) => ({
        value: item.id,
        label: item.id,
      }));
      allOptions = allOptions.concat(options);
    }

    return allOptions;
  }

  function transformConsumptionData(data, selectedMsisdns, startDate, endDate) {
    // Generate all dates in range (yyyy-MM-dd strings)
    var allDates = getAllDatesInRange(startDate, endDate);

    // prepare map: msisdn -> date -> amount (amount = 1 per usage)
    // Initialize with zeros
    var usageMap = {};
    selectedMsisdns.forEach((msisdn) => {
      usageMap[msisdn] = {};
      allDates.forEach((date) => {
        usageMap[msisdn][date] = 0;
      });
    });

    // fill usage amounts from data
    data.forEach(({ msisdn, date, amount }) => {
      if (usageMap[msisdn]) {
        usageMap[msisdn][date] = amount; // usually 1
      }
    });

    var cumulativeMap = {};
    // compute cumulative sums per msisdn over dates, reset at new month
    selectedMsisdns.forEach((msisdn) => {
      cumulativeMap[msisdn] = {};
      let cumulativeSum = 0;
      let prevMonth = null;

      allDates.forEach((date) => {
        const currentMonth = date.slice(0, 7); // "YYYY-MM"

        if (prevMonth !== currentMonth) {
          // New month detected, reset cumulative sum
          cumulativeSum = 0;
          prevMonth = currentMonth;
        }

        cumulativeSum += usageMap[msisdn][date];
        cumulativeMap[msisdn][date] = cumulativeSum;
      });
    });

    // build final array for chart: one object per date, with msisdn keys and cumulative values
    var chartData = allDates.map((date) => {
      var obj = { date };
      selectedMsisdns.forEach((msisdn) => {
        obj[msisdn] = cumulativeMap[msisdn][date];
      });
      return obj;
    });

    return chartData;
  }

  function getAllDatesInRange(startDate, endDate) {
    const dates = [];
    let currentDate = startDate;
    while (!isBefore(endDate, currentDate)) {
      dates.push(format(currentDate, "yyyy-MM-dd"));
      currentDate = addDays(currentDate, 1);
    }
    return dates;
  }
  const [loading, setLoading] = useState(false);

  const handleExport = async () => {
    if (selectedMsisdns.length === 0) return;

    setLoading(true);

    try {
      const msisdnList = selectedMsisdns.some((opt) => opt.value === "all")
        ? msisdnOptions
        : selectedMsisdns;

      const range = `${format(selectedWeek?.startDate, "yyyy-MM-dd")}|${format(
        selectedWeek?.endDate,
        "yyyy-MM-dd"
      )}`;
      const msisdnQuery = msisdnList
        .map((entry) => `msisdns=${entry.value}`)
        .join("&");

      const response = await getConsumptionForMsisdn(
        companyId,
        range,
        msisdnQuery
      );

      const consumptionData = response.data.consumption || [];

      // Map API consumption by msisdn for quick lookup
      const consumptionMap = new Map(consumptionData.map((u) => [u.msisdn, u]));

      // Build complete consumption list: include all selected MSISDNs with empty data if missing
      const completeConsumptionList = msisdnList.map(({ value }) => {
        return (
          consumptionMap.get(value) || {
            msisdn: value,
            consumption: [], // empty consumption - zero usage
            total: 0,
          }
        );
      });

      exportRangeConsumptionToExcel(
        { consumption: completeConsumptionList },
        selectedWeek?.startDate,
        selectedWeek?.endDate,
        msisdnList.map((opt) => opt.value),
        t
      );
    } catch (error) {
      console.error("Export failed:", error);
    }

    setLoading(false);
  };

  const getReasonLabel = (reason, t) => {
    switch (reason) {
      case "Manual":
        return t("creditManagement.reason.manual");
      case "MsisdnConsumption":
        return t("creditManagement.reason.msisdnConsumption");
      case "Bundle":
        return t("creditManagement.reason.bundle");
      default:
        return t("creditManagement.reason.unknown");
    }
  };

  return (
    <div className={css["purchase-chart-card"]}>
      <div className={css["purchase-chart-header"]}>
        <div className={css["purchase-chart-title-header"]}>
          <h3>{graphName}</h3>
          {type == "balance" ? (
            <>
              <Button
                onClick={() => {
                  setViewMode((prev) => (prev === "chart" ? "table" : "chart"));
                }}
                className="ml-4"
                style={{
                  background: "whitesmoke",
                  border: "1px solid #CCC",
                  borderRadius: "4px",
                  width: "50px",
                  height: "38px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  padding: "0",
                }}
                title={
                  viewMode === "chart"
                    ? t("creditManagement.showTable")
                    : t("creditManagement.showChart")
                }
              >
                <FontAwesomeIcon
                  icon={viewMode === "chart" ? faTable : faChartLine}
                  style={{ color: "#333" }}
                />
              </Button>
            </>
          ) : null}
        </div>

        {type === "msisdnConsumption" ? (
          <>
            <div className="d-flex align-items-center justify-content-between">
              <WeekSelector
                onChange={(weekInfo) => {
                  setSelectedWeek(weekInfo);
                }}
                selectAll={true}
              />
              <ClaimGuard requiredClaim={GET_BILLING_REPORT}>
                <Button
                  onClick={handleExport}
                  disabled={loading || selectedMsisdns.length === 0}
                  className="ml-4 mt-4"
                  style={{
                    background: "whitesmoke",
                    border: "1px solid #CCC",
                    borderRadius: "4px",
                    width: "50px",
                    height: "38px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    padding: "0",
                  }}
                >
                  <FontAwesomeIcon
                    icon={faDownload}
                    style={{ color: "#333" }}
                  />
                </Button>
              </ClaimGuard>
            </div>
          </>
        ) : (
          <>
            <div className={css["date-picker-row"]}>
              <div className={css["date-picker-group"]}>
                <label>{t("creditManagement.startDate")} </label>
                <DatePicker
                  selected={startDate}
                  onChange={(date) => date && setStartDate(date)}
                  selectsStart
                  startDate={startDate}
                  endDate={endDate}
                  dateFormat="yyyy-MM-dd"
                  className={css["styled-datepicker"]}
                />
              </div>
              <div className={css["date-picker-group"]}>
                <label>{t("creditManagement.endDate")} </label>
                <DatePicker
                  selected={endDate}
                  onChange={(date) => date && setEndDate(date)}
                  selectsEnd
                  startDate={startDate}
                  endDate={endDate}
                  minDate={startDate}
                  dateFormat="yyyy-MM-dd"
                  className={css["styled-datepicker"]}
                />
              </div>
            </div>
          </>
        )}

        {type == "msisdnConsumption" ? (
          <>
            <FormLabel className="mt-2">Msisdns</FormLabel>

            <Select
              isMulti
              options={msisdnOptions}
              value={selectedMsisdns}
              placeholder={t("creditManagement.select")}
              onChange={(selectedOptions) => {
                if (!selectedOptions || selectedOptions.length === 0) {
                  setSelectedMsisdns([]);
                  fetchConsumptionReport([]);
                  setData([]);
                  setMsisdns([]);
                } else {
                  // Check if "Select All" is selected
                  const isSelectAll = selectedOptions.some(
                    (option) => option.value === "all"
                  );

                  if (isSelectAll) {
                    // Select all MSISDN options except the "Select All" itself
                    const allMsisdns = msisdnOptions.filter(
                      (option) => option.value !== "all"
                    );
                    setSelectedMsisdns(allMsisdns);
                    fetchConsumptionReport(allMsisdns);
                    setData([]);
                    setMsisdns([]);
                  } else {
                    setSelectedMsisdns(selectedOptions);
                    fetchConsumptionReport(selectedOptions);
                    setData([]);
                    setMsisdns([]);
                  }
                }
              }}
              className="mb-3"
            />
          </>
        ) : null}
      </div>

      <div className={css["purchase-chart-body"]}>
        {data.length === 0 ? (
          <p>{t("creditManagement.noData")}</p>
        ) : (
          <ResponsiveContainer width="100%" height={450}>
            {type == "consumption" ? (
              <BarChart data={data}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip
                  content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      const { msisdn, date, amount } = payload[0].payload;

                      if (type === "purchase") {
                        return (
                          <div
                            className="custom-tooltip"
                            style={{
                              backgroundColor: "#fff",
                              padding: "8px",
                              border: "1px solid #ccc",
                            }}
                          >
                            <p>{date}</p>
                            <p>
                              <strong>{t("creditManagement.amount")}</strong>{" "}
                              {amount.toFixed(2)}
                            </p>
                          </div>
                        );
                      } else if (type === "msisdnConsumption") {
                        return (
                          <div
                            className="custom-tooltip"
                            style={{
                              backgroundColor: "#fff",
                              padding: "8px",
                              border: "1px solid #ccc",
                            }}
                          >
                            <p>
                              <strong>MSISDN:</strong> {msisdn}
                            </p>
                            <p>
                              <strong>{t("creditManagement.count")}:</strong>{" "}
                              {amount}
                            </p>
                          </div>
                        );
                      }
                    }
                    return null;
                  }}
                />
                <Bar dataKey="amount" fill="#4e73df" radius={[4, 4, 0, 0]} />
              </BarChart>
            ) : type == "balance" ? (
              viewMode == "chart" ? (
                <LineChart data={data}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    tickFormatter={(tick) => format(parseISO(tick), "dd MMM")}
                  />
                  <YAxis domain={[0, "auto"]} />
                  <Tooltip
                    labelFormatter={(label) =>
                      format(parseISO(label), "dd MMM yyyy, HH:mm")
                    }
                  />
                  <Line
                    type="monotone"
                    dataKey="amount"
                    stroke="#4e73df"
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    activeDot={{ r: 5 }}
                  />
                </LineChart>
              ) : (
                <div
                  style={{
                    maxHeight: "450px",
                    overflowY: "auto",
                    overflowX: "auto",
                    marginBottom: "1rem", // prevent overlap with next element
                  }}
                >
                  <Table>
                    <thead>
                      <tr>
                        <th>{t("creditManagement.date")}</th>
                        <th>{t("creditManagement.amount")}</th>
                        <th>{t("creditManagement.balanceBefore")}</th>
                        <th>{t("creditManagement.balanceAfter")}</th>
                        <th>{t("creditManagement.reason.reason")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {balances.map((entry, index) => (
                        <tr key={index}>
                          <td>
                            {format(
                              parseISO(entry.timestamp),
                              "dd MMM yyyy, HH:mm"
                            )}
                          </td>
                          <td>{entry.amount}</td>
                          <td>
                            {entry.balanceAfterModification - entry.amount}
                          </td>
                          <td>{entry.balanceAfterModification}</td>
                          <td>{getReasonLabel(entry.reason, t)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )
            ) : type == "msisdnConsumption" ? (
              <LineChart data={data}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="date"
                  tickFormatter={(tick) => format(parseISO(tick), "dd MMM")}
                />
                <YAxis domain={["dataMin - 2", "dataMax"]} />
                <Tooltip />
                <Legend />
                {msisdns.map((msisdn, index) => (
                  <Line
                    dataKey={msisdn}
                    key={msisdn}
                    type="monotone"
                    dataKey={msisdn}
                    stroke={stringToHslColor(msisdn)}
                    dot={false}
                  />
                ))}
              </LineChart>
            ) : null}
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};

export default ManagementCharts;
